/**
 * User & group contracts — DESIGN.md §9.3, §12.2 (R21).
 */
import { z } from 'zod';
import { USER_ROLES } from '../enums.js';
import { emailSchema, name, optionalText, paginationSchema, uuidSchema } from './common.js';
import { passwordSchema } from './auth.js';
/**
 * The colour on the *read* side — #53.
 *
 * A plain string, not the `AVATAR_COLOURS` enum, and deliberately so. The
 * write path validates against the enum, so only a palette name is ever
 * stored; reading is where a value written by an older or newer build turns
 * up, and a response that fails to serialize because someone's avatar colour
 * was retired is a worse outcome than an avatar that falls back to its
 * default. `avatarColourFor` resolves anything unrecognised, which is the same
 * bargain §6.6 makes for a status colour chosen by an admin.
 */
export const avatarColorSchema = z.string().nullable();
/** The user shape returned everywhere a person appears (pickers, avatars, cards). */
export const userSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    /** #53 — the chosen colour, or null for the id-derived default. */
    avatarColor: avatarColorSchema,
    role: z.enum(USER_ROLES),
    timezone: z.string(),
    isActive: z.boolean(),
    hasPassword: z.boolean(),
    hasGoogle: z.boolean(),
    groups: z.array(z.object({ id: uuidSchema, name: z.string() })),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Trimmed shape embedded in cards, comments, and audit lines. */
export const userRefSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    /**
     * #53 — carried on the *ref*, not just the full user, because the colour is
     * only useful where the face is: a comment byline, a card row, an activity
     * line. `null` means the default derived from `id`, which is why the id has
     * to travel with it.
     */
    avatarColor: avatarColorSchema,
    isActive: z.boolean(),
});
export const listUsersQuerySchema = paginationSchema.extend({
    active: z
        .enum(['true', 'false', 'all'])
        .default('true')
        .transform((v) => (v === 'all' ? null : v === 'true')),
    q: optionalText(200),
});
export const createUserRequestSchema = z
    .object({
    email: emailSchema,
    name: name(200),
    role: z.enum(USER_ROLES).default('member'),
    groupIds: z.array(uuidSchema).max(50).default([]),
    timezone: z.string().min(1).max(64).optional(),
    /**
     * Omit to send an invite email instead (§9.1). Setting a password here is
     * the break-glass path for a user who cannot receive mail.
     */
    password: passwordSchema.optional(),
    sendInvite: z.boolean().default(true),
})
    .strict();
export const updateUserRequestSchema = z
    .object({
    name: name(200).optional(),
    role: z.enum(USER_ROLES).optional(),
    timezone: z.string().min(1).max(64).optional(),
    groupIds: z.array(uuidSchema).max(50).optional(),
    /** Admin-set password reset; the user can change it afterwards. */
    password: passwordSchema.optional(),
})
    .strict();
export const groupSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    createdAt: z.string(),
});
export const groupWithMembersSchema = groupSchema.extend({
    members: z.array(userRefSchema),
});
export const createGroupRequestSchema = z
    .object({
    name: name(100),
    description: optionalText(1000),
    memberIds: z.array(uuidSchema).max(500).default([]),
})
    .strict();
export const updateGroupRequestSchema = z
    .object({
    name: name(100).optional(),
    description: optionalText(1000),
})
    .strict();
export const setGroupMembersRequestSchema = z
    .object({ memberIds: z.array(uuidSchema).max(500) })
    .strict();
//# sourceMappingURL=users.js.map